import { useEffect, useState } from 'react';

/**
 * Cuánto le queda de vida a la RESERVA (no a la cotización).
 *
 * Es el otro reloj del flujo y no se puede derivar del de la tarifa: aquel
 * cuenta un TTL local fijo desde que React Query aceptó la respuesta
 * (`useFareCountdown`), y este cuenta hasta un instante ABSOLUTO que fija el
 * proveedor (`expiresAt` del booking), que va de minutos a días según el
 * operador. Por eso el umbral de aviso y el formato son propios.
 *
 * El tono es neutro casi todo el tiempo y vira al final: un reloj en ámbar
 * durante dos días no dice nada.
 */
const ENDING_THRESHOLD_MS = 5 * 60 * 1000;

export type HoldCountdownStatus = 'running' | 'ending' | 'expired' | 'unknown';

export interface HoldCountdown {
  status: HoldCountdownStatus;
  remainingMs: number;
  /** `M:SS`, o `H:MM:SS` cuando falta una hora o más. Vacío si no se sabe. */
  label: string;
}

const UNKNOWN: HoldCountdown = { status: 'unknown', remainingMs: 0, label: '' };

function format(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const padded = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${padded}` : `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * ¿Hay un vencimiento que se pueda LEER? Es la condición de la que sale el
 * reloj, y por eso se exporta: quien decida que una fila muestra contador
 * tiene que preguntar exactamente esto, o prometería un reloj que después no
 * se dibuja y dejaría la celda vacía (§21.9).
 */
export function isReadableExpiry(expiresAt: string | undefined | null): boolean {
  return (
    typeof expiresAt === 'string' &&
    expiresAt.trim() !== '' &&
    Number.isFinite(Date.parse(expiresAt))
  );
}

/**
 * `expiresAt` llega como instante ISO ya validado contra el contrato y
 * persistido por el backend (`rutero-provider-dates.ts` rechaza en origen lo
 * que no sea `YYYY-MM-DDTHH:MM:SS.sssZ` de calendario real), así que acá no se
 * revalida el formato: se lee. Lo que sí se contempla es la ausencia — hay
 * reservas sin vencimiento publicado — y ahí el estado es DESCONOCIDO, nunca
 * "vencido": pintar de rojo una reserva viva porque no llegó la fecha es peor
 * que no mostrar el reloj.
 */
export function holdCountdownAt(
  expiresAt: string | undefined | null,
  now: number,
): HoldCountdown {
  if (!isReadableExpiry(expiresAt) || !Number.isFinite(now)) return UNKNOWN;
  const expiry = Date.parse(expiresAt as string);

  const remainingMs = expiry - now;
  const status: HoldCountdownStatus =
    remainingMs <= 0 ? 'expired' : remainingMs <= ENDING_THRESHOLD_MS ? 'ending' : 'running';

  return { status, remainingMs, label: status === 'expired' ? '' : format(remainingMs) };
}

export function useHoldCountdown(expiresAt?: string | null): HoldCountdown {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (typeof expiresAt !== 'string' || !expiresAt.trim()) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  return holdCountdownAt(expiresAt, now);
}
